const PING_URL = '/bff/health';
const PING_TIMEOUT_MS = 5_000;

// Adaptive polling intervals
const STABLE_INTERVAL_MS = 120_000; // 2 min when state is confirmed
const VERIFYING_INTERVAL_MS = 10_000; // 10s when confirming a state change

// Consecutive-result thresholds to prevent single-packet false flips
const FAILURES_BEFORE_OFFLINE = 2;
const SUCCESSES_BEFORE_STABLE = 2;

type ConnectivityListener = (isOnline: boolean) => void;

let isOnline: boolean = navigator.onLine;
let verifying = false;
let consecutiveFailures = 0;
let consecutiveSuccesses = 0;
let timerId: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<ConnectivityListener>();

async function checkConnectivity(): Promise<boolean> {
  if (!navigator.onLine) return false;

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

    const response = await fetch(PING_URL, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal
    });

    clearTimeout(timeout);
    return response.ok;
  } catch {
    return false;
  }
}

function setOnlineState(value: boolean): void {
  if (isOnline !== value) {
    isOnline = value;
    for (const listener of listeners) {
      try {
        listener(value);
      } catch {
        // swallow per-listener errors
      }
    }
  }
}

function scheduleNextPoll(): void {
  if (timerId !== null) clearTimeout(timerId);
  const interval = verifying ? VERIFYING_INTERVAL_MS : STABLE_INTERVAL_MS;
  timerId = setTimeout(poll, interval);
}

async function poll(): Promise<void> {
  const reachable = await checkConnectivity();

  if (reachable) {
    consecutiveFailures = 0;
    consecutiveSuccesses++;
    setOnlineState(true);

    if (consecutiveSuccesses >= SUCCESSES_BEFORE_STABLE) {
      verifying = false;
    }
  } else {
    consecutiveSuccesses = 0;
    consecutiveFailures++;

    if (isOnline && consecutiveFailures < FAILURES_BEFORE_OFFLINE) {
      // First failure — rapid poll to confirm before declaring offline
      verifying = true;
    } else {
      // Confirmed offline (or already was) — slow poll as safety net
      setOnlineState(false);
      verifying = false;
    }
  }

  scheduleNextPoll();
}

export function getIsOnline(): boolean {
  return isOnline;
}

export function subscribe(listener: ConnectivityListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export async function startMonitoring(): Promise<void> {
  // Initial check — await so getIsOnline() is accurate before React renders
  const result = await checkConnectivity();
  setOnlineState(result);

  scheduleNextPoll();

  // Browser online event — verify with a real ping
  window.addEventListener('online', handleBrowserOnline);
  // Browser offline event — trust immediately
  window.addEventListener('offline', handleBrowserOffline);

  // Network type changes (Chrome/Edge only) — verify immediately
  const connection = (navigator as any).connection;
  if (connection?.addEventListener) {
    connection.addEventListener('change', handleConnectionChange);
  }
}

export function stopMonitoring(): void {
  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }
  window.removeEventListener('online', handleBrowserOnline);
  window.removeEventListener('offline', handleBrowserOffline);

  const connection = (navigator as any).connection;
  if (connection?.removeEventListener) {
    connection.removeEventListener('change', handleConnectionChange);
  }
}

function handleBrowserOnline(): void {
  verifying = true;
  consecutiveFailures = 0;
  consecutiveSuccesses = 0;
  poll();
}

function handleBrowserOffline(): void {
  verifying = false;
  consecutiveFailures = FAILURES_BEFORE_OFFLINE;
  consecutiveSuccesses = 0;
  setOnlineState(false);
  scheduleNextPoll();
}

function handleConnectionChange(): void {
  verifying = true;
  consecutiveFailures = 0;
  consecutiveSuccesses = 0;
  poll();
}
